import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  helperText?: string;
  /**
   * Disable the shake animation on error
   * @default false
   */
  disableShake?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, helperText, disableShake = false, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, ...props }, ref) => {
    const inputId = React.useId();
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const [shouldShake, setShouldShake] = React.useState(false);
    const prevErrorRef = React.useRef(error);

    // Trigger shake animation when error transitions from false to true
    React.useEffect(() => {
      if (error && !prevErrorRef.current && !disableShake) {
        setShouldShake(true);
        const timer = setTimeout(() => setShouldShake(false), 500);
        return () => clearTimeout(timer);
      }
      prevErrorRef.current = error;
    }, [error, disableShake]);

    // Combine provided aria-describedby with helper text ID
    const describedBy = [ariaDescribedBy, helperId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        <input
          type={type}
          className={cn(
            "flex h-12 w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm text-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none focus-visible:border-slate-300 disabled:cursor-not-allowed disabled:opacity-60 transition-all duration-200 text-left",
            error && "border-red-600 focus:border-red-600 focus-visible:border-red-600",
            shouldShake && "animate-shake",
            className,
          )}
          ref={ref}
          aria-invalid={ariaInvalid ?? error ?? undefined}
          aria-describedby={describedBy}
          {...props}
        />
        {helperText && (
          <p
            id={helperId}
            className={cn(
              "mt-1.5 text-xs",
              error ? "text-destructive" : "text-muted-foreground"
            )}
            role={error ? "alert" : undefined}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
